import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useLocation, useParams } from "react-router-dom";
import Switch from "react-switch";
import {
  useGetTrackingStepDetailQuery,
  useSubmitTrackingStepMutation,
} from "store/services/clients/assessmentTreatmentPlan";
import {
  dateFormat,
  errorMessageToast,
  getAge,
  printNameInitials,
  promiseToast,
  userFullName,
  userFullNameReverse,
} from "helpers/common";
import LoadingIndicator from "components/shared/LoadingIndicator";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingOverlay from "components/shared/LoadingOverlay";
import {
  useCreateGoalTrackingMutation,
  useUpdateGoalTrackingMutation,
} from "store/services/clients/assessmentTreatmentPlan/goals/trackGoals";
import LoadingButton from "components/shared/LoadingButton";
import GoalTrackingForm from "./GoalTrackingForm";
import { getFirstGuardian } from "helpers/clients";
import moment from "moment";
import { groupGoalTrackings } from "helpers/assessment";

const GoalsTracking = ({ onSubmit, onNavigateBack, clientType }) => {
  const { assessmentId, clientId } = useParams();

  const [assessmentVirtualChecked, setAssessmentVirtualChecked] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [isSubmitting, setSubmitting] = useState(false);

  const [trackGoal, { isLoading: goalTrackLoading }] = useCreateGoalTrackingMutation();
  const [updateGoalTrack, { isLoading: goalTrackUpdateLoading }] = useUpdateGoalTrackingMutation();
  const [submitStep, { isLoading: stepTwoSubmitting }] = useSubmitTrackingStepMutation();
  const { data, isLoading, isFetching, isError, refetch } = useGetTrackingStepDetailQuery({ assessmentId, clientId, clientType });
  const assessmentGoals = data?.assesmentData?.reportGoals;
  const assessmentAssignedId = data?.assesment?.id;
  const teacherDetails = getFirstGuardian(data?.client_detail?.client_guardian_mapping, "teacher");
  const parentDetails = getFirstGuardian(data?.client_detail?.client_guardian_mapping, "parent");
  const goalsTrackingAdded = assessmentGoals?.every(x => x?.goalTracking?.length > 0);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!data) {
      return;
    }
    setAssessmentVirtualChecked(data?.assesment?.assesment_location === "virtual");
  }, [data])

  const formRefs = useMemo(() => {
    if (!assessmentGoals?.length) return [];
    return assessmentGoals?.map((goal) => {
      return createRef();
    });
  }, [assessmentGoals]);

  const handleChangeAssessmentLocation = (checked) => {
    setAssessmentVirtualChecked(checked);
  };

  const saveGoalTrackings = useCallback(
    async (goalTrackings) => {
      try {
        let payload = {
          goal_trackings: groupGoalTrackings(goalTrackings)
        };

        return promiseToast(
          trackGoal({
            assessmentId: assessmentAssignedId,
            data: payload,
            clientType
          }).unwrap(),
          { toastId: "createGoalTrack" }
        );

      } catch (err) {
        console.error("Error tracking goal", err);
      }
    },
    [assessmentAssignedId]
  );

  const updateGoalTracking = useCallback(
    async (tracked, goalId, trackedId) => {
      try {
        let payload = {
          tracked,
          assesment_goal: goalId,
        };
        return promiseToast(
          updateGoalTrack({
            trackedId,
            assessmentId: assessmentAssignedId,
            data: payload,
            clientType
          }).unwrap(),
          { toastId: "updateGoalTrack" }
        );
      } catch (err) {
        console.error("Error updating goal tracking", err);
      }
    },
    [assessmentAssignedId]
  );

  const handleSubmitStep = async () => {
    try {
      setSubmitting(true);
      if (!assessmentGoals?.length) {
        throw "Assessment goals not found";
      }
      if (!goalsTrackingAdded) {
        return;
      };
      let payload = {
        assesment_location: assessmentVirtualChecked ? "virtual" : "in-person",
        goals: [],
      };

      assessmentGoals?.forEach((goal, index) => {
        let values = formRefs[index].current.values;
        if(values?.hasUnsavedChanges) {
          setSubmissionError("There are unsaved changes, please save them before proceeding");
          throw new Error("Form contains unsaved changes");
        }
        
        payload.goals.push({
          goal_id: goal.id,
          goal_notes: values?.goal_notes,
        })
      });

      setSubmissionError(null);

      let response = await submitStep({
        assessmentAssignedId,
        data: payload,
        clientType
      });

      onSubmit(response);
    } catch (err) {
      setSubmitting(false);
      console.error("Error submitting step two", err);
    }finally{
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="startSession position-relative py-3">
        <Container fluid>
          <Row>
            <Col lg="12" className="my-2">
              <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center">
                <li className="">
                  <Link
                    className="themeBlue fw-sbold"
                    style={{ textDecoration: "underline", fontSize: 14 }}
                    to="/client/school/"
                  >
                    Client
                  </Link>
                </li>
                <li className="">
                  <h6 className="m-0 fw-bold themeBlue" style={{ fontSize: 14 }}>/ Start Session</h6>
                </li>
              </ul>
            </Col>
            {isLoading ? (
              <LoadingIndicator />
            ) : isError ? (
              <ErrorComponent />
            ) : (
              <>
                {isFetching && <LoadingOverlay />}
                <Col lg="12" className="my-2">
                  <div className="py-2">
                    <div
                      className="cardCstm border rounded-3"
                      style={{ background: "#F0FEFF" }}
                    >
                      <div className="border-bottom p-3 d-flex align-items-center justify-content-between gap-10 flex-wrap">
                        <div className="left d-flex align-items-center gap-10">
                          <div className="d-flex align-items-center gap-10">
                            <div
                              className="imgWrp themeBlue fw-bold flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                height: 35,
                                width: 35,
                                background: "#B46C9F",
                              }}
                            >
                              {printNameInitials(data)}
                            </div>
                            <div className="content">
                              <h6 className="m-0 fw-bold themeBlue">
                                {userFullNameReverse(data)}
                              </h6>
                            </div>
                          </div>
                          <div className=" d-flex align-items-center">
                            <p className="m-0 text-muted mx-1">In-person</p>
                            <Switch
                              uncheckedIcon={""}
                              checkedIcon={""}
                              onHandleColor={"#A3DF00"}
                              offHandleColor={"#A3DF00"}
                              onColor={"#E6E6E6"}
                              offColor={"#E6E6E6"}
                              height={18}
                              width={40}
                              onChange={handleChangeAssessmentLocation}
                              checked={assessmentVirtualChecked}
                            />
                            <p className="m-0 text-muted mx-1">Virtual</p>
                          </div>
                        </div>
                        <div className="right d-flex align-items-center gap-10">
                          <span className="icn">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="20"
                              viewBox="0 0 18 20"
                              fill="none"
                            >
                              <path
                                d="M2.30775 19.5C1.80258 19.5 1.375 19.325 1.025 18.975C0.675 18.625 0.5 18.1974 0.5 17.6923V4.30777C0.5 3.8026 0.675 3.37502 1.025 3.02502C1.375 2.67502 1.80258 2.50002 2.30775 2.50002H3.69225V0.384766H5.23075V2.50002H12.8077V0.384766H14.3077V2.50002H15.6923C16.1974 2.50002 16.625 2.67502 16.975 3.02502C17.325 3.37502 17.5 3.8026 17.5 4.30777V17.6923C17.5 18.1974 17.325 18.625 16.975 18.975C16.625 19.325 16.1974 19.5 15.6923 19.5H2.30775ZM2.30775 18H15.6923C15.7692 18 15.8398 17.9679 15.9038 17.9038C15.9679 17.8398 16 17.7693 16 17.6923V8.30777H2V17.6923C2 17.7693 2.03208 17.8398 2.09625 17.9038C2.16025 17.9679 2.23075 18 2.30775 18ZM2 6.80777H16V4.30777C16 4.23077 15.9679 4.16026 15.9038 4.09626C15.8398 4.0321 15.7692 4.00002 15.6923 4.00002H2.30775C2.23075 4.00002 2.16025 4.0321 2.09625 4.09626C2.03208 4.16026 2 4.23077 2 4.30777V6.80777ZM9 12.077C8.75517 12.077 8.5465 11.9908 8.374 11.8183C8.20167 11.6459 8.1155 11.4373 8.1155 11.1923C8.1155 10.9474 8.20167 10.7388 8.374 10.5663C8.5465 10.3939 8.75517 10.3078 9 10.3078C9.24483 10.3078 9.4535 10.3939 9.626 10.5663C9.79833 10.7388 9.8845 10.9474 9.8845 11.1923C9.8845 11.4373 9.79833 11.6459 9.626 11.8183C9.4535 11.9908 9.24483 12.077 9 12.077ZM5 12.077C4.75517 12.077 4.5465 11.9908 4.374 11.8183C4.20167 11.6459 4.1155 11.4373 4.1155 11.1923C4.1155 10.9474 4.20167 10.7388 4.374 10.5663C4.5465 10.3939 4.75517 10.3078 5 10.3078C5.24483 10.3078 5.4535 10.3939 5.626 10.5663C5.79833 10.7388 5.8845 10.9474 5.8845 11.1923C5.8845 11.4373 5.79833 11.6459 5.626 11.8183C5.4535 11.9908 5.24483 12.077 5 12.077ZM13 12.077C12.7552 12.077 12.5465 11.9908 12.374 11.8183C12.2017 11.6459 12.1155 11.4373 12.1155 11.1923C12.1155 10.9474 12.2017 10.7388 12.374 10.5663C12.5465 10.3939 12.7552 10.3078 13 10.3078C13.2448 10.3078 13.4535 10.3939 13.626 10.5663C13.7983 10.7388 13.8845 10.9474 13.8845 11.1923C13.8845 11.4373 13.7983 11.6459 13.626 11.8183C13.4535 11.9908 13.2448 12.077 13 12.077ZM9 16C8.75517 16 8.5465 15.9138 8.374 15.7413C8.20167 15.5689 8.1155 15.3603 8.1155 15.1155C8.1155 14.8705 8.20167 14.6618 8.374 14.4895C8.5465 14.317 8.75517 14.2308 9 14.2308C9.24483 14.2308 9.4535 14.317 9.626 14.4895C9.79833 14.6618 9.8845 14.8705 9.8845 15.1155C9.8845 15.3603 9.79833 15.5689 9.626 15.7413C9.4535 15.9138 9.24483 16 9 16ZM5 16C4.75517 16 4.5465 15.9138 4.374 15.7413C4.20167 15.5689 4.1155 15.3603 4.1155 15.1155C4.1155 14.8705 4.20167 14.6618 4.374 14.4895C4.5465 14.317 4.75517 14.2308 5 14.2308C5.24483 14.2308 5.4535 14.317 5.626 14.4895C5.79833 14.6618 5.8845 14.8705 5.8845 15.1155C5.8845 15.3603 5.79833 15.5689 5.626 15.7413C5.4535 15.9138 5.24483 16 5 16ZM13 16C12.7552 16 12.5465 15.9138 12.374 15.7413C12.2017 15.5689 12.1155 15.3603 12.1155 15.1155C12.1155 14.8705 12.2017 14.6618 12.374 14.4895C12.5465 14.317 12.7552 14.2308 13 14.2308C13.2448 14.2308 13.4535 14.317 13.626 14.4895C13.7983 14.6618 13.8845 14.8705 13.8845 15.1155C13.8845 15.3603 13.7983 15.5689 13.626 15.7413C13.4535 15.9138 13.2448 16 13 16Z"
                                fill="#071E3E"
                              />
                            </svg>
                          </span>
                          <p className="m-0"> {moment().format("MMM DD, YYYY | hh:mm A")}</p>

                        </div>
                      </div>
                      <div className="bottom p-2">
                        <Row>
                          <Col lg="2" md="3" sm="4" className="my-2 col-6">
                            <h6
                              className="m-0 d-flex align-items-start themeBlue fw-bold"
                              style={{ fontSize: "14px" }}
                            >
                              <span
                                className="icn me-1"
                                style={{ color: "rgb(61, 160, 170)" }}
                              >
                                ■{" "}
                              </span>{" "}
                              DOB
                            </h6>
                            <div className="m-0 themeBlue">
                              {dateFormat(data?.client_detail?.dob)}
                            </div>
                          </Col>
                          <Col lg="2" md="3" sm="4" className="my-2 col-6">
                            <h6
                              className="m-0 d-flex align-items-start themeBlue fw-bold"
                              style={{ fontSize: "14px" }}
                            >
                              <span
                                className="icn me-1"
                                style={{ color: "rgb(61, 160, 170)" }}
                              >
                                ■{" "}
                              </span>{" "}
                              Age
                            </h6>
                            <div className="m-0 themeBlue">
                              {getAge(data?.client_detail?.dob)}
                            </div>
                          </Col>
                          <Col lg="2" md="3" sm="4" className="my-2 col-6">
                            <h6
                              className="m-0 d-flex align-items-start themeBlue fw-bold"
                              style={{ fontSize: "14px" }}
                            >
                              <span
                                className="icn me-1"
                                style={{ color: "rgb(61, 160, 170)" }}
                              >
                                ■{" "}
                              </span>{" "}
                              Grade
                            </h6>
                            <div className="m-0 themeBlue">
                              Grade {data?.client_detail?.grade}
                            </div>
                          </Col>
                          {clientType == "school" && <Col lg="2" md="3" sm="4" className="my-2 col-6">
                            <h6
                              className="m-0 d-flex align-items-start themeBlue fw-bold"
                              style={{ fontSize: "14px" }}
                            >
                              <span
                                className="icn me-1"
                                style={{ color: "rgb(61, 160, 170)" }}
                              >
                                ■{" "}
                              </span>{" "}
                              School
                            </h6>
                            <div className="m-0 themeBlue">
                              {
                                data?.client_detail?.client_school_detail
                                  ?.client_school?.name
                              }
                            </div>
                          </Col>}

                          <Col lg="2" md="3" sm="4" className="my-2 col-6">
                            <h6
                              className="m-0 d-flex align-items-start themeBlue fw-bold"
                              style={{ fontSize: "14px" }}
                            >
                              <span
                                className="icn me-1"
                                style={{ color: "rgb(61, 160, 170)" }}
                              >
                                ■{" "}
                              </span>{" "}
                              {clientType == "school" ? "Teacher" : "Parent"}
                            </h6>
                            <div className="m-0 themeBlue text-capitalize">
                              {clientType == "school" ? userFullName(teacherDetails) : userFullName(parentDetails)}
                            </div>
                          </Col>
                          {/* <Col lg="2" md="3" sm="4" className="my-2 col-6">
                            <h6
                              className="m-0 d-flex align-items-start themeBlue fw-bold"
                              style={{ fontSize: "13px" }}
                            >
                              <span
                                className="icn me-1"
                                style={{ color: "rgb(61, 160, 170)" }}
                              >
                                ■{" "}
                              </span>{" "}
                              Division
                            </h6>
                            <p className="m-0 themeBlue">
                              {
                                data?.client_detail?.client_school_detail
                                  ?.client_school_division?.title
                              }
                            </p>
                          </Col> */}
                        </Row>
                      </div>
                    </div>
                  </div>
                  <div>
                    {assessmentGoals?.length > 0 ? (
                      assessmentGoals.map((goal, index) => {
                        return (
                          <GoalTrackingForm
                            key={index}
                            innerRef={formRefs[index]}
                            goal={goal}
                            // handleTrackGoal={handleTrackGoal}
                            updateGoalTracking={updateGoalTracking}
                            saveGoalTrackings={saveGoalTrackings}
                            goalTrackLoading={goalTrackLoading}
                            refetch={refetch}
                          />
                        );
                      })
                    ) : (
                      <p>No Goals Added!</p>
                    )}
                  </div>
                  {submissionError && (
                    <div className="py-2">
                      <div className="error">{submissionError}</div>
                    </div>
                  )}
                  <div className="btnWrpper mt-3 d-flex gap-10">
                    <Button
                      style={{
                        background: "rgb(69, 163, 165)",
                        borderColor: "rgb(69, 163, 165)",
                      }}
                      onClick={onNavigateBack}
                      disabled={!!isSubmitting || isFetching}
                      className="d-flex align-items-center justify-content-center commonBtn fw-sbold"
                    >
                      Back
                    </Button>
                    <LoadingButton
                      onClick={handleSubmitStep}
                      className="d-flex align-items-center justify-content-center commonBtn fw-sbold"
                      disabled={isFetching}
                      // isLoading={stepTwoSubmitting || isFetching}
                      isLoading={isSubmitting}
                    >
                      Save & Next
                    </LoadingButton>
                  </div>
                </Col>
              </>
            )}
          </Row>
        </Container>
      </section>
    </>
  );
};

export default GoalsTracking;
